import React from 'react';

// "Does this filter select the columns the BOM viewer reads from it?" — shown under each
// filter picker on Admin → Settings, answered by POST /api/bom/check-filter.
//
// WHY. A saved Orcanos filter returns only the columns it SELECTS, and an unselected column
// reads back EMPTY rather than as an error — so a filter missing `parent_original_id` gives
// an assembly that expands into nothing, with no message anywhere. Until now the only way to
// find that out was to save the settings, open the tree and click a chevron.
//
// It ADVISES, it does not refuse. Unlike the Training panel's version (Critical Note #34),
// nothing here blocks Save: an admin may knowingly run without a cost or status column, and
// half a configuration saved is still progress. The one thing it must never do is claim a
// clean bill it did not earn — `checked: false` is rendered as "couldn't check", never as
// "nothing missing".

const SEVERITY_TEXT = {
  required: 'Required',
  recommended: 'Recommended',
  optional: 'Optional',
};

const REASON_TEXT = {
  no_type: 'Pick the work item type first.',
  no_version: 'Pick the project version first.',
  empty: 'This filter returned no items, so its columns can’t be read.',
  error: 'Couldn’t read this filter.',
};

export default function BomFilterCheck({ result, busy, hasFilter, onRecheck }) {
  if (!hasFilter) return null;

  if (busy) {
    return <div className="settings-hint bomfc-line">Checking the filter&rsquo;s columns&hellip;</div>;
  }
  if (!result) {
    return (
      <div className="settings-hint bomfc-line">
        <button type="button" className="bomfc-link" onClick={onRecheck}>Check columns</button>
      </div>
    );
  }

  // Not checked is NOT "all clear" — say which, and why.
  if (!result.checked) {
    return (
      <div className="settings-hint bomfc-line bomset-warn">
        {REASON_TEXT[result.reason] || 'Couldn’t check this filter.'}
        {result.message && result.reason === 'error' && <> {result.message}</>}
        {' '}
        <button type="button" className="bomfc-link" onClick={onRecheck}>Check again</button>
      </div>
    );
  }

  const missing = [
    ...(result.missing?.required || []),
    ...(result.missing?.recommended || []),
    ...(result.missing?.optional || []),
  ];

  if (!missing.length) {
    return (
      <div className="settings-hint bomfc-line bomfc-ok">
        &#10003; This filter selects every column the BOM viewer needs.
        {' '}
        <button type="button" className="bomfc-link" onClick={onRecheck}>Re-check</button>
      </div>
    );
  }

  return (
    <div className="bomfc">
      <div className="bomfc-head">
        This filter doesn&rsquo;t select {missing.length === 1 ? 'a column' : 'some columns'} the
        BOM viewer reads. Add {missing.length === 1 ? 'it' : 'them'} to the filter in Orcanos,
        then <button type="button" className="bomfc-link" onClick={onRecheck}>re-check</button>.
      </div>
      <ul className="bomfc-list">
        {missing.map((f) => (
          <li key={f.role} className={`bomfc-item bomfc-${f.severity}`}>
            <span className="bomfc-badge">{SEVERITY_TEXT[f.severity] || f.severity}</span>
            <span className="bomfc-title">{f.title}</span>
            {f.name && <span className="bomfc-name">{f.name}</span>}
            <div className="bomfc-why">{f.why}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
